/**
 * Phase B — Tenant Lockdown
 *
 * Reads `tenant_lockdown` for the currently scoped tenant and exposes
 * boolean kill-switches the rest of the app reads to block UX (hide
 * storefront, disable checkout, pause orders, hide products, etc.).
 *
 * When no tenant is scoped, all flags are `false` (main site behaviour
 * is unchanged).
 */
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTenantStore } from "@/lib/tenantContext";

export interface TenantLockdown {
  storefront_hidden: boolean;
  products_hidden: boolean;
  checkout_disabled: boolean;
  orders_paused: boolean;
  customer_access_disabled: boolean;
  staff_access_disabled: boolean;
  message: string | null;
}

const EMPTY: TenantLockdown = {
  storefront_hidden: false, products_hidden: false, checkout_disabled: false,
  orders_paused: false, customer_access_disabled: false, staff_access_disabled: false,
  message: null,
};

const cache = new Map<string, { at: number; row: TenantLockdown }>();
const TTL = 30_000;

async function fetchLockdown(tenantId: string | null): Promise<TenantLockdown> {
  if (!tenantId) return EMPTY;
  const c = cache.get(tenantId);
  if (c && Date.now() - c.at < TTL) return c.row;
  try {
    const { data } = await (supabase as any)
      .from("tenant_lockdown")
      .select("storefront_hidden, products_hidden, checkout_disabled, orders_paused, customer_access_disabled, staff_access_disabled, message")
      .eq("tenant_id", tenantId)
      .maybeSingle();
    const row: TenantLockdown = data ? { ...EMPTY, ...(data as Partial<TenantLockdown>) } : EMPTY;
    cache.set(tenantId, { at: Date.now(), row });
    return row;
  } catch {
    return EMPTY;
  }
}

export function invalidateTenantLockdown(tenantId?: string | null) {
  if (tenantId) cache.delete(tenantId); else cache.clear();
}

export function useTenantLockdown() {
  const tenantId = useTenantStore(s => s.tenantId);
  const [lockdown, setLockdown] = useState<TenantLockdown>(EMPTY);
  const [loading, setLoading] = useState<boolean>(!!tenantId);

  const refresh = useCallback(async () => {
    setLoading(true);
    const row = await fetchLockdown(tenantId);
    setLockdown(row);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (!tenantId) return;
    const ch = supabase.channel(`lockdown-${tenantId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "tenant_lockdown", filter: `tenant_id=eq.${tenantId}` },
        () => { invalidateTenantLockdown(tenantId); void refresh(); })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [tenantId, refresh]);

  return { ...lockdown, loading, refresh, tenantId };
}
